import { requestService } from '@services/request';
import { collectionMap } from '@vincerosoft/assessfluence-types';
import {
  DocumentData,
  DocumentSnapshot,
  FieldPath,
  Primitive,
  UpdateData
} from 'firebase/firestore';

type WhereFilterOpModified =
  '$lt' | '$lte' | '$eq' | '$ne' | '$gte' | '$gt' | '$includes' | '$in' | '$nin' | '$any';

type LogicalOperator = '$and' | '$or';

type LogicalOperatorsQuery = {
  [key in LogicalOperator]?: QueryConstraintModified[];
};

type ImplicitWhereQuery = {
  [key: string]: { [key in WhereFilterOpModified]?: unknown } | Primitive;
};

export type QueryConstraintModified = ImplicitWhereQuery | LogicalOperatorsQuery;

export type ApiRequestOptions = {
  endAt?: string | number | DocumentSnapshot<unknown, DocumentData>;
  endBefore?: unknown[] | string;
  limit?: number;
  offset?: number;
  orderBy?: string[];
  select?: (string | FieldPath)[];
  startAfter?: unknown[] | string;
  startAt?: string | number | DocumentSnapshot<unknown, DocumentData>;
  count?: boolean;
};

type OptionalBoolean = true | undefined;

const isEmptyValue = (value: unknown) => {
  if (value == null) {
    return true;
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  if (typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>).length === 0;
  }

  return false;
};

const toQuery = (filter?: QueryConstraintModified, options?: ApiRequestOptions) => {
  const query: Record<string, string> = {};

  if (!isEmptyValue(filter)) {
    query.filter = JSON.stringify(filter);
  }

  if (!isEmptyValue(options)) {
    query.options = JSON.stringify(options);
  }

  return query;
};

function apiFactory() {
  async function getDocsInChunks<T>(
    collection: keyof typeof collectionMap | string,
    itemsToChunk: {
      field: string;
      operator: '$in' | '$nin';
      values: string[];
    },
    chunkSize = 30,
    filter: QueryConstraintModified = {},
    options: ApiRequestOptions = {},
    inProgressStore?: (partial: T[]) => void | Promise<void>
  ): Promise<T[]> {
    const normalizedChunkSize = Math.min(Math.max(chunkSize || 30, 1), 30);

    if (itemsToChunk.values.length === 0) {
      return Promise.resolve([]);
    }

    const combinedResults: T[] = [];
    for (let i = 0; i < itemsToChunk.values.length; i += normalizedChunkSize) {
      const chunk = itemsToChunk.values.slice(i, i + normalizedChunkSize);
      const chunkFilter: QueryConstraintModified = {
        ...filter,
        [itemsToChunk.field]: {
          [itemsToChunk.operator]: chunk
        }
      };

      const chunkResult = await getDocs<T>(collection, chunkFilter, options);
      combinedResults.push(...chunkResult);
      if (inProgressStore) {
        await inProgressStore(chunkResult);
      }
    }

    return combinedResults;
  }

  function getDocs<T>(
    collection: keyof typeof collectionMap | string,
    filter: QueryConstraintModified = {},
    options: ApiRequestOptions = {}
  ) {
    return requestService<T[]>(`/admin/db/${String(collection)}`, {
      query: toQuery(filter, options)
    });
  }

  function count(
    collection: keyof typeof collectionMap | string,
    filter: QueryConstraintModified = {},
    options: ApiRequestOptions = {}
  ) {
    return requestService<number>(`/admin/db/${String(collection)}`, {
      query: toQuery(filter, { ...options, count: true })
    });
  }

  function getDoc<T>(collection: keyof typeof collectionMap | string, docId: string) {
    return requestService<T>(`/admin/db/${String(collection)}/${docId}`);
  }

  function addDoc<T>(
    collection: keyof typeof collectionMap | string,
    data: T,
    returnDocument?: OptionalBoolean
  ) {
    type Response = OptionalBoolean extends true
      ? T & { created_at: Date }
      : { id: string; created_at: Date };

    return requestService<Response>(`/admin/db/${String(collection)}`, {
      method: 'POST',
      body: data,
      query: toQuery({}, { options: { returnDocument } } as unknown as ApiRequestOptions)
    });
  }

  function updateDoc<T>(
    collection: keyof typeof collectionMap | string,
    docId: string,
    data: UpdateData<T>,
    returnDocument?: OptionalBoolean
  ) {
    type Response = OptionalBoolean extends true
      ? T & { updated_at: Date }
      : { id: string; updatedAt: Date };

    return requestService<Response>(`/admin/db/${String(collection)}/${docId}`, {
      method: 'PATCH',
      body: data,
      query: toQuery({}, { options: { returnDocument } } as unknown as ApiRequestOptions)
    });
  }

  function setDoc<T>(
    collection: keyof typeof collectionMap | string,
    docId: string,
    data: T,
    returnDocument?: OptionalBoolean
  ) {
    type Response = (T & { updated_at: Date }) | { id: string; updatedAt: Date };

    return requestService<Response>(`/admin/db/${String(collection)}/${docId}`, {
      method: 'PUT',
      body: data,
      query: toQuery({}, { options: { returnDocument } } as unknown as ApiRequestOptions)
    });
  }

  function deleteDoc(collection: keyof typeof collectionMap | string, docId: string) {
    return requestService<{ id: string; deletedAt: Date }>(
      `/admin/db/${String(collection)}/${docId}`,
      {
        method: 'DELETE'
      }
    );
  }

  return {
    getDocs,
    count,
    getDoc,
    addDoc,
    updateDoc,
    setDoc,
    deleteDoc,
    getDocsInChunks
  };
}

export const apiService = apiFactory();
